/**
 * Özet panosu charts (v0.1.257): visits by source and Etsy revenue, day by day over the period and the one before it
 * (the earlier days drawn lighter). The chart rules of the panel audit hold: the takeaway on top, the busiest and the
 * last day written on the chart, the picked day said in the readout row (the last one until a tap, a click or an arrow
 * key picks another), dates as "2 Kas", one colour per source and metric (SOURCE_COLOURS, --data-neutral for money,
 * --metric-clicks for Google clicks), never the accent, and every number in a screen reader table too.
 */

import { SITE_SOURCE_GROUP_LABELS, SITE_SOURCE_GROUPS, type SiteDay } from '@twin/contracts';
import type { OzetRange, OzetSummaryDay } from '../api/ozet.js';
import { formatIsoDay, formatNumber, formatUsd } from '../lib/format.js';
import {
  BarValueLabels,
  ChartFrame,
  ChartReadout,
  ChartTakeaway,
  labelledPoints,
  pickHandlers,
  useChartPick,
} from './chart-kit.js';
import { sourceSlots } from './site-sources-chart.js';

const WIDTH = 600;
const HEIGHT = 160;
const GAP = 2;

function n(value: number): string {
  return formatNumber(value);
}

export function centsText(cents: number): string {
  return formatUsd(cents * 10_000, 'auto');
}

/** The slots of the stacked chart, from the days' GA4 sources (the Site page's colours and folding). */
function slotsOf(days: readonly OzetSummaryDay[]) {
  // sourceSlots reads only the source sessions of each day.
  return sourceSlots(days.map((day) => ({ sourceSessions: day.sourceSessions }) as SiteDay));
}

type Slot = ReturnType<typeof slotsOf>[number];

function amountOf(day: OzetSummaryDay, slot: Slot, slots: readonly Slot[]): number {
  if (slot.key !== 'rest') return day.sourceSessions[slot.key] ?? 0;
  const named = new Set(slots.map((item) => item.key));
  return SITE_SOURCE_GROUPS.filter((group) => !named.has(group)).reduce(
    (sum, group) => sum + (day.sourceSessions[group] ?? 0),
    0,
  );
}

function shortLabel(slot: Slot): string {
  return slot.label.replace(/ \(.*\)$/, '');
}

function barX(index: number, count: number): { x: number; bar: number } {
  const bar = (WIDTH - GAP * (count - 1)) / count;
  return { x: index * (bar + GAP), bar };
}

function inPeriod(day: OzetSummaryDay, period: OzetRange): boolean {
  return day.day >= period.from && day.day <= period.to;
}

/**
 * The busiest and the last counted day's totals over the plot (BarValueLabels' placement): a day GA4 has not given
 * yet carries no label, so it never reads as 0.
 */
export function CountedValueLabels({
  values,
  counted,
  max,
  text = n,
}: {
  values: readonly number[];
  counted: readonly boolean[];
  max: number;
  text?: (value: number) => string;
}) {
  const lastCounted = counted.lastIndexOf(true);
  const count = values.length;
  return (
    <>
      {labelledPoints(values.slice(0, lastCounted + 1)).map((index) => {
        const value = values[index] ?? 0;
        const left = ((index + 0.5) / Math.max(1, count)) * 100;
        const bottom = (value / Math.max(1, max)) * 100;
        const edge =
          index === count - 1 || left > 92
            ? 'chart-value chart-value--end'
            : left < 8
              ? 'chart-value chart-value--start'
              : 'chart-value';
        return (
          <span
            key={index}
            className={edge}
            style={{ left: `${left}%`, bottom: `min(${bottom}%, calc(100% - 16px))` }}
            aria-hidden="true"
          >
            {text(value)}
          </span>
        );
      })}
    </>
  );
}

/** Siteye kim nereden geldi: GA4 sessions per day stacked by source; a day GA4 has not given yet is a thin mark. */
export function OzetVisitsChart({ daily, current }: { daily: readonly OzetSummaryDay[]; current: OzetRange }) {
  const pick = useChartPick(daily.length);
  const counted = daily.filter((day) => day.sessions !== null);
  const slots = slotsOf(counted);
  if (counted.length === 0 || slots.length === 0) {
    return <p className="sx-empty">Google Analytics'te bu günler için henüz kaynak bilgisi yok.</p>;
  }
  const totals = daily.map((day) =>
    day.sessions === null ? 0 : slots.reduce((s, slot) => s + amountOf(day, slot, slots), 0),
  );
  const max = Math.max(1, ...totals);
  const inCurrent = daily.filter((day) => inPeriod(day, current) && day.sessions !== null);
  const currentSlots = slotsOf(inCurrent);
  const currentTotal = inCurrent.reduce(
    (s, day) => s + currentSlots.reduce((t, slot) => t + amountOf(day, slot, currentSlots), 0),
    0,
  );
  const lead = currentSlots[0];
  const shown = daily[pick.index];
  const readout =
    shown === undefined
      ? ''
      : shown.sessions === null
        ? `${formatIsoDay(shown.day)}: ziyaretçi sayımı ertesi sabah gelir`
        : `${formatIsoDay(shown.day)}: ${n(totals[pick.index] ?? 0)} oturum · ${
            slots
              .map((slot) => ({ slot, value: amountOf(shown, slot, slots) }))
              .filter((item) => item.value > 0)
              .map((item) => `${shortLabel(item.slot)} ${n(item.value)}`)
              .join(' · ') || 'oturum yok'
          }`;
  const titleId = 'oz-visits-title';
  return (
    <figure className="sx-src oz-chart">
      <ChartTakeaway>
        {inCurrent.length === 0 || lead === undefined
          ? 'Bu dönemin ziyaretçi sayımı ertesi sabah gelir'
          : `Bu dönemde ${n(currentTotal)} oturum; en çok ${shortLabel(lead)} getirdi (${n(lead.total)})`}
      </ChartTakeaway>
      <ChartFrame label="Siteye gelenler: gün seç" count={daily.length} pick={pick} valueText={readout}>
        <div className="sx-src__plot">
          <svg
            className="sx-src__svg"
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="none"
            role="img"
            aria-labelledby={titleId}
          >
            <title id={titleId}>
              {`Günlük ziyaretçi, kaynağa göre: ${formatIsoDay(daily[0]?.day ?? '')} ile ${formatIsoDay(daily.at(-1)?.day ?? '')} arası. ${slots.map((slot) => `${slot.label} ${n(slot.total)}`).join(', ')}.`}
            </title>
            {[0.5, 1].map((share) => (
              <line
                key={share}
                className="sx-src__grid"
                x1={0}
                x2={WIDTH}
                y1={HEIGHT - 1 - share * (HEIGHT - 4)}
                y2={HEIGHT - 1 - share * (HEIGHT - 4)}
              />
            ))}
            <line className="au-bars__base" x1={0} y1={HEIGHT - 0.5} x2={WIDTH} y2={HEIGHT - 0.5} />
            {daily.map((day, index) => {
              const { x, bar } = barX(index, daily.length);
              let top = HEIGHT - 1;
              const classes = [pick.index === index ? 'is-focus' : '', inPeriod(day, current) ? '' : 'oz-prev']
                .filter(Boolean)
                .join(' ');
              return (
                <g key={day.day} className={classes || undefined}>
                  <rect
                    className="sx-src__hit"
                    x={x}
                    y={0}
                    width={bar + GAP}
                    height={HEIGHT}
                    {...pickHandlers(pick, index)}
                  />
                  {day.sessions === null ? (
                    <rect
                      className="oz-chart__pending"
                      x={x}
                      y={HEIGHT - 4}
                      width={bar}
                      height={3}
                      pointerEvents="none"
                    />
                  ) : (
                    slots.map((slot) => {
                      const value = amountOf(day, slot, slots);
                      if (value === 0) return null;
                      const h = (value / max) * (HEIGHT - 4);
                      top -= h;
                      return (
                        <rect
                          key={slot.key}
                          x={x}
                          y={top + 0.5}
                          width={bar}
                          height={Math.max(0.5, h - 1)}
                          fill={slot.colour}
                          pointerEvents="none"
                        />
                      );
                    })
                  )}
                </g>
              );
            })}
          </svg>
          <CountedValueLabels values={totals} counted={daily.map((day) => day.sessions !== null)} max={max} />
        </div>
      </ChartFrame>
      <ChartReadout>{readout}</ChartReadout>
      <figcaption className="au-bars__cap" aria-hidden="true">
        <span>{formatIsoDay(daily[0]?.day ?? '')}</span>
        <span>{formatIsoDay(daily.at(-1)?.day ?? '')}</span>
      </figcaption>
      <ul className="sx-src__legend" aria-label="Kaynaklar ve getirdikleri kişi">
        {slots.map((slot) => (
          <li key={slot.key}>
            <span className="sx-src__swatch" style={{ background: slot.colour }} aria-hidden="true" />
            <span>{slot.label}</span>
            <span className="mono">{n(slot.total)}</span>
          </li>
        ))}
      </ul>
      <div className="sr-only">
        <table>
          <caption>Günlük ziyaretçi, kaynağa göre</caption>
          <thead>
            <tr>
              <th scope="col">Gün</th>
              {slots.map((slot) => (
                <th key={slot.key} scope="col">
                  {SITE_SOURCE_GROUP_LABELS[slot.key as keyof typeof SITE_SOURCE_GROUP_LABELS] ?? slot.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {daily.map((day) => (
              <tr key={day.day}>
                <th scope="row">{formatIsoDay(day.day)}</th>
                {slots.map((slot) => (
                  <td key={slot.key}>{day.sessions === null ? '-' : amountOf(day, slot, slots)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

/** Satış, gün gün: Etsy revenue per day as bars, Google clicks as a line over them (null days leave a gap). */
export function OzetSalesChart({ daily, current }: { daily: readonly OzetSummaryDay[]; current: OzetRange }) {
  const pick = useChartPick(daily.length);
  if (daily.length === 0) return <p className="sx-empty">Bu dönem için gün yok.</p>;
  const cents = daily.map((day) => day.revenueCents);
  const max = Math.max(1, ...cents);
  const clicks = daily.map((day) => day.googleClicks);
  const clickMax = Math.max(1, ...clicks.map((value) => value ?? 0));
  const inCurrent = daily.filter((day) => inPeriod(day, current));
  const revenue = inCurrent.reduce((s, day) => s + day.revenueCents, 0);
  const orders = inCurrent.reduce((s, day) => s + day.orders, 0);
  const shown = daily[pick.index];
  const readout =
    shown === undefined
      ? ''
      : `${formatIsoDay(shown.day)}: ${centsText(shown.revenueCents)} · ${n(shown.orders)} sipariş · Google ${
          shown.googleClicks === null ? 'henüz gelmedi' : `${n(shown.googleClicks)} tıklama`
        }`;
  let path = '';
  let pen = false;
  clicks.forEach((value, index) => {
    if (value === null) {
      pen = false;
      return;
    }
    const { x, bar } = barX(index, daily.length);
    const y = HEIGHT - 2 - (value / clickMax) * (HEIGHT - 10);
    path += `${pen ? 'L' : 'M'}${(x + bar / 2).toFixed(1)} ${y.toFixed(1)} `;
    pen = true;
  });
  const titleId = 'oz-sales-title';
  return (
    <figure className="sx-src oz-chart">
      <ChartTakeaway>
        {orders === 0 ? 'Bu dönemde henüz Etsy siparişi yok' : `Bu dönemde ${centsText(revenue)}, ${n(orders)} sipariş`}
      </ChartTakeaway>
      <ChartFrame label="Satış: gün seç" count={daily.length} pick={pick} valueText={readout}>
        <div className="sx-src__plot">
          <svg
            className="sx-src__svg"
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="none"
            role="img"
            aria-labelledby={titleId}
          >
            <title id={titleId}>
              {`Günlük Etsy geliri ve Google tıklaması: ${formatIsoDay(daily[0]?.day ?? '')} ile ${formatIsoDay(daily.at(-1)?.day ?? '')} arası.`}
            </title>
            {[0.5, 1].map((share) => (
              <line
                key={share}
                className="sx-src__grid"
                x1={0}
                x2={WIDTH}
                y1={HEIGHT - 1 - share * (HEIGHT - 4)}
                y2={HEIGHT - 1 - share * (HEIGHT - 4)}
              />
            ))}
            <line className="au-bars__base" x1={0} y1={HEIGHT - 0.5} x2={WIDTH} y2={HEIGHT - 0.5} />
            {daily.map((day, index) => {
              const { x, bar } = barX(index, daily.length);
              const h = (day.revenueCents / max) * (HEIGHT - 4);
              const classes = [pick.index === index ? 'is-focus' : '', inPeriod(day, current) ? '' : 'oz-prev']
                .filter(Boolean)
                .join(' ');
              return (
                <g key={day.day} className={classes || undefined}>
                  <rect
                    className="sx-src__hit"
                    x={x}
                    y={0}
                    width={bar + GAP}
                    height={HEIGHT}
                    {...pickHandlers(pick, index)}
                  />
                  {day.revenueCents === 0 ? null : (
                    <rect
                      className="oz-chart__bar"
                      x={x}
                      y={HEIGHT - 1 - h}
                      width={bar}
                      height={Math.max(0.5, h)}
                      pointerEvents="none"
                    />
                  )}
                </g>
              );
            })}
            {path === '' ? null : <path className="oz-chart__line" d={path.trim()} pointerEvents="none" />}
          </svg>
          <BarValueLabels values={cents} max={max} text={centsText} />
        </div>
      </ChartFrame>
      <ChartReadout>{readout}</ChartReadout>
      <figcaption className="au-bars__cap" aria-hidden="true">
        <span>{formatIsoDay(daily[0]?.day ?? '')}</span>
        <span>{formatIsoDay(daily.at(-1)?.day ?? '')}</span>
      </figcaption>
      <ul className="sx-src__legend" aria-label="Grafikteki ölçüler">
        <li>
          <span className="sx-src__swatch oz-swatch--money" aria-hidden="true" />
          <span>Etsy geliri</span>
        </li>
        <li>
          <span className="sx-src__swatch oz-swatch--clicks" aria-hidden="true" />
          <span>Google tıklama</span>
        </li>
      </ul>
      <div className="sr-only">
        <table>
          <caption>Günlük Etsy geliri, sipariş ve Google tıklaması</caption>
          <thead>
            <tr>
              <th scope="col">Gün</th>
              <th scope="col">Gelir</th>
              <th scope="col">Sipariş</th>
              <th scope="col">Google tıklama</th>
            </tr>
          </thead>
          <tbody>
            {daily.map((day) => (
              <tr key={day.day}>
                <th scope="row">{formatIsoDay(day.day)}</th>
                <td>{centsText(day.revenueCents)}</td>
                <td>{day.orders}</td>
                <td>{day.googleClicks === null ? '-' : day.googleClicks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
